import React, { useState, useEffect } from 'react';
import { Plus, FileText, Download, Trash2, Eye, FolderOpen } from 'lucide-react';
import { Card, Button, Input, Select, Table, Modal, Badge, ConfirmDialog } from '../components/ui';
import { documentosService } from '../services/api';
import { formatDate } from '../utils/formatters';
import { CATEGORIAS_DOCUMENTO } from '../utils/constants';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const DocumentosPage = () => {
  const { hasPermission } = useAuth();
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filterCategoria, setFilterCategoria] = useState('');
  const [form, setForm] = useState({
    nombre: '',
    descripcion: '',
    categoria: 'OTROS',
    version: '1.0',
    archivo: null
  });

  useEffect(() => {
    loadData();
  }, [filterCategoria]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await documentosService.getAll({ categoria: filterCategoria });
      setDocumentos(response.data.data || []);
    } catch (error) {
      toast.error('Error al cargar documentos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('nombre', form.nombre);
      formData.append('descripcion', form.descripcion);
      formData.append('categoria', form.categoria);
      formData.append('version', form.version);
      if (form.archivo) {
        formData.append('archivo', form.archivo);
      }

      await documentosService.upload(formData);
      toast.success('Documento subido');
      setModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al subir documento');
    }
  };

  const handleDelete = async () => {
    try {
      await documentosService.delete(selected.id);
      toast.success('Documento eliminado');
      setConfirmOpen(false);
      setSelected(null);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setForm({
      nombre: '',
      descripcion: '',
      categoria: 'OTROS',
      version: '1.0',
      archivo: null
    });
  };
  const handleDownload = async (doc) => {
    try {
      const response = await documentosService.download(doc.id);
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Obtener extensión del archivo o de la columna extension
      let extension = doc.extension || '';
      if (!extension && doc.archivo) {
        const parts = doc.archivo.split('.');
        if (parts.length > 1) {
          extension = parts.pop();
        }
      }
      
      // Construir nombre del archivo
      let filename = doc.nombre;
      if (extension && !filename.toLowerCase().endsWith('.' + extension.toLowerCase())) {
        filename = doc.nombre + '.' + extension;
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Documento descargado');
    } catch (error) {
      toast.error('Error al descargar documento');
    }
  };
  

  const getCategoriaColor = (cat) => {
    const colors = {
      REGLAMENTOS: 'purple',
      ACTAS: 'info',
      FINANCIERO: 'success',
      SEGURIDAD: 'warning',
      CONTRATOS: 'danger',
      OTROS: 'default'
    };
    return colors[cat] || 'default';
  };

  const columns = [
    { 
      header: 'Documento', 
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/20">
            <FileText className="text-blue-400" size={20} />
          </div>
          <div>
            <p className="font-medium text-white">{row.nombre}</p>
            <p className="text-xs text-slate-500">{row.descripcion || 'Sin descripción'}</p>
          </div>
        </div>
      )
    },
    { 
      header: 'Categoría', 
      render: (row) => (
        <Badge variant={getCategoriaColor(row.categoria)}>
          {CATEGORIAS_DOCUMENTO.find(c => c.value === row.categoria)?.label || row.categoria}
        </Badge>
      )
    },
    { header: 'Versión', render: (row) => <span className="text-slate-400">v{row.version}</span> },
    { header: 'Tamaño', render: (row) => <span className="text-slate-400">{row.tamano}</span> },
    { header: 'Fecha', render: (row) => formatDate(row.created_at) },
    { header: 'Descargas', render: (row) => <span className="text-slate-400">{row.descargas || 0}</span> },
    { 
      header: 'Acciones', 
      render: (row) => (
        <div className="flex gap-2">
          <button 
            onClick={() => handleDownload(row)}
            className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-emerald-400"
            title="Descargar"
          >
            <Download size={16} />
          </button>
          {hasPermission('PRESIDENTE', 'SECRETARIO') && (
            <button 
              onClick={() => { setSelected(row); setConfirmOpen(true); }} 
              className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-rose-400"
              title="Eliminar"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      )
    }
  ];

  const docsByCategoria = CATEGORIAS_DOCUMENTO.map(cat => ({
    ...cat,
    count: documentos.filter(d => d.categoria === cat.value).length
  }));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Documentos</h1>
          <p className="text-slate-400">Biblioteca de documentos del edificio</p>
        </div>
        {hasPermission('PRESIDENTE', 'SECRETARIO') && (
          <Button icon={Plus} onClick={() => { resetForm(); setModalOpen(true); }}>
            Subir Documento
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card 
          className={`p-4 cursor-pointer transition-all ${!filterCategoria ? 'border-emerald-500' : ''}`}
          onClick={() => setFilterCategoria('')}
        >
          <div className="flex items-center gap-3">
            <FolderOpen className="text-slate-400" size={20} />
            <div>
              <p className="text-white font-medium">Todos</p>
              <p className="text-xs text-slate-500">{documentos.length} docs</p>
            </div>
          </div>
        </Card>
        {docsByCategoria.map((cat) => (
          <Card 
            key={cat.value}
            className={`p-4 cursor-pointer transition-all ${filterCategoria === cat.value ? 'border-emerald-500' : ''}`}
            onClick={() => setFilterCategoria(cat.value)}
          >
            <div>
              <p className="text-white font-medium text-sm">{cat.label}</p>
              <p className="text-xs text-slate-500">{cat.count} docs</p>
            </div>
          </Card>
        ))}
      </div>

      <Card className="overflow-hidden">
        <Table columns={columns} data={documentos} loading={loading} emptyMessage="No hay documentos" />
      </Card>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Subir Documento">
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nombre del documento"
            value={form.nombre}
            onChange={(e) => setForm({ ...form, nombre: e.target.value })}
            required
          />
          <Input
            label="Descripción"
            value={form.descripcion}
            onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Categoría"
              value={form.categoria}
              onChange={(e) => setForm({ ...form, categoria: e.target.value })}
              options={CATEGORIAS_DOCUMENTO}
              required
            />
            <Input
              label="Versión"
              value={form.version}
              onChange={(e) => setForm({ ...form, version: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Archivo</label>
            <input
              type="file"
              onChange={(e) => setForm({ ...form, archivo: e.target.files[0] })}
              className="w-full text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-emerald-500 file:text-white file:cursor-pointer"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit">Subir</Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Eliminar Documento"
        message={`¿Estás seguro de eliminar "${selected?.nombre}"?`}
      />
    </div>
  );
};

export default DocumentosPage;